import { PermissionGroup, PermissionGroupUser } from '@kalos/kalos-rpc';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  MultipleSelect,
  toast,
} from '@kalos/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { queryKeys } from '../../../hooks/react-query/constants';
import { UserClientService } from '../../../tools/helpers';
import { type PermissionSection } from './permissionSections';

interface Props {
  userId: number;
  userName: string;
  section: PermissionSection;
  existingIds: number[];
  isOwnerSU: boolean;
  onClose: () => void;
}

export const AddPermissionsDialog = ({
  userId,
  userName,
  section,
  existingIds,
  isOwnerSU,
  onClose,
}: Props) => {
  const queryClient = useQueryClient();
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);

  const permissionGroupsQuery = useQuery({
    queryKey: ['permissionGroups'],
    queryFn: async () => {
      const req = PermissionGroup.create();
      req.isActive = true;
      return (await UserClientService.BatchGetPermission(req)).results;
    },
  });

  const addMutation = useMutation({
    mutationFn: async (groupIds: string[]) => {
      for (const groupId of groupIds) {
        const req = PermissionGroupUser.create();
        req.userId = userId;
        req.permissionGroupId = Number(groupId);
        await UserClientService.AddUserToPermissionGroup(req);
      }
    },
    onSuccess: async (_, groupIds) => {
      toast({
        title: `Added ${groupIds.length} permission${groupIds.length === 1 ? '' : 's'} to ${userName}`,
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [queryKeys.user.root] }),
        queryClient.invalidateQueries({ queryKey: [queryKeys.user.permissionGroupUser.root] }),
      ]);
      onClose();
    },
    onError: () => {
      toast({
        title: 'Failed to add permissions',
        description:
          'Some permissions may not have been added. Please re-open the dialog and retry.',
        variant: 'destructive',
      });
    },
  });

  const existing = new Set(existingIds);
  const options = (permissionGroupsQuery.data ?? [])
    .filter((group) => group.type === section.type)
    .filter((group) => !existing.has(group.id))
    // Only an SU may grant the SU privilege itself
    .filter((group) => group.name !== 'SU' || isOwnerSU)
    .map((group) => ({
      value: String(group.id),
      label: group.description ? `${group.name} - ${group.description}` : group.name,
    }));

  const busy = addMutation.isPending;

  return (
    <Dialog open onOpenChange={(open) => !open && !busy && onClose()}>
      <DialogContent
        blurActiveElementOnScroll={false}
        className="min-h-[26rem] content-start bg-white sm:max-w-lg"
      >
        <DialogHeader>
          <DialogTitle>
            Add {section.label.toLowerCase()} to {userName}
          </DialogTitle>
          <DialogDescription>
            Select the {section.type}(s) to add for this employee.
          </DialogDescription>
        </DialogHeader>
        {permissionGroupsQuery.isError ? (
          <p className="text-destructive">Failed to load permissions. Please try again.</p>
        ) : (
          <MultipleSelect
            options={options}
            selected={selectedGroupIds}
            onChange={setSelectedGroupIds}
            loading={permissionGroupsQuery.isLoading}
            disabled={busy}
            placeholder={`Select ${section.type}(s)...`}
            listClassName="max-h-72"
          />
        )}
        <DialogFooter>
          <Button variant="outline" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={selectedGroupIds.length === 0 || busy}
            onClick={() => addMutation.mutate(selectedGroupIds)}
          >
            {busy ? 'Adding...' : 'Add Selected'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
